// Хранилище сессии бекофиса.
// САМ ТОКЕН в localStorage НЕ хранится — он в httpOnly-cookie (bo_session),
// недоступной JS (защита от кражи через XSS). В localStorage держим только
// несекретные claims (role/club_id/exp) для UI-гейтов; реальную авторизацию
// и роли проверяет бэкенд по cookie. Реактивная обёртка — в AuthContext.

// support/manager — кросс-клубные роли сотрудников Help (SupportHub), заводятся
// через modules/support-integration, входят через /sso (см. pages/Sso.tsx).
export type StaffRole = 'super_admin' | 'owner' | 'admin' | 'viewer' | 'accountant' | 'support' | 'manager'

/**
 * Роли БЕЗ своего клуба (club_id = null) — клуб выбирается вручную в шапке.
 * super_admin — площадка целиком; support/manager — сотрудники Help, каждый со
 * своим узким набором разделов (см. ROLE_PATHS в layout/AppLayout.tsx).
 */
export const CROSS_CLUB_ROLES: readonly StaffRole[] = ['super_admin', 'support', 'manager']

/** true — у роли нет собственного клуба, и запросы должны нести ?clubId. */
export function isCrossClubRole(role: StaffRole | null | undefined): boolean {
  return role != null && CROSS_CLUB_ROLES.includes(role)
}

/** Полезная нагрузка JWT сотрудника (то, что подписывает бэкенд). */
export interface StaffClaims {
  /** subject — id сотрудника */
  sub: string
  /** id клуба; null/отсутствует для super_admin (кросс-клубный доступ) */
  club_id: string | null
  role: StaffRole
  /** опциональные стандартные поля JWT */
  email?: string
  exp?: number
  iat?: number
}

const CLAIMS_KEY = 'bo.auth.claims'

// Токен сессии в ПАМЯТИ (не в localStorage): отправляется как Bearer, когда
// httpOnly-cookie заблокирована браузером (Safari ITP / строгий 3rd-party-cookie —
// бекофис и бэкенд на разных доменах *.vercel.app). Не переживает перезагрузку и
// недоступен после неё — в отличие от localStorage его нельзя украсть «потом».
let memToken: string | null = null
export function setMemToken(token: string | null): void {
  memToken = token
}
export function getMemToken(): string | null {
  return memToken
}

export function getClaims(): StaffClaims | null {
  try {
    const raw = localStorage.getItem(CLAIMS_KEY)
    return raw ? (JSON.parse(raw) as StaffClaims) : null
  } catch {
    return null
  }
}

export function saveSession(claims: StaffClaims): void {
  localStorage.setItem(CLAIMS_KEY, JSON.stringify(claims))
}

export function clearSession(): void {
  memToken = null
  localStorage.removeItem(CLAIMS_KEY)
}

/**
 * Декодирует payload JWT без проверки подписи (проверку делает бэкенд).
 * Нужен только чтобы достать club_id/role/exp для UI-гейтов.
 */
export function decodeJwt(token: string): StaffClaims | null {
  const parts = token.split('.')
  if (parts.length < 2) return null
  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(payload)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    )
    return JSON.parse(json) as StaffClaims
  } catch {
    return null
  }
}

/** true, если у токена есть exp и он в прошлом. */
export function isExpired(claims: StaffClaims | null): boolean {
  if (!claims?.exp) return false
  return claims.exp * 1000 <= Date.now()
}
